import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { 
  ArrowRight, 
  ArrowUp,
  Link2, 
  Zap, 
  Target, 
  Quote, 
  Users,
  Play,
  Check,
  ChevronRight,
  ChevronDown,
  Share2,
  Image,
  Video,
  Sparkles,
  Gift,
  Star,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  CreditCard,
  Wallet,
  Mic,
  Layers,
  Award,
  Search,
  Trophy,
  Building2,
  Briefcase,
  Handshake,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ShareModal from "@/components/ShareModal";
import TestimonialsSection from "@/components/TestimonialsSection";
import TechnologyPartners from "@/components/TechnologyPartners";
import InfluencerLeaderboard from "@/components/InfluencerLeaderboard";

// Scroll to Top Button Component
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-viraly-orange hover:bg-viraly-orange/90 text-white shadow-lg shadow-viraly-orange/30 flex items-center justify-center transition-all hover:scale-110"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

// All 71 supported languages grouped by region
const ALL_LANGUAGES = {
  "Major Global": ["English", "Español", "中文", "हिन्दी", "العربية", "Português", "Français", "Русский", "日本語", "Deutsch"],
  "Asian": ["한국어", "Tiếng Việt", "ไทย", "Bahasa Indonesia", "Bahasa Melayu", "Filipino", "မြန်မာ", "ខ្មែរ", "ລາວ"],
  "South Asian": ["বাংলা", "தமிழ்", "తెలుగు", "मराठी", "ગુજરાતી", "ಕನ್ನಡ", "മലയാളം", "ਪੰਜਾਬੀ", "ଓଡ଼ିଆ", "অসমীয়া", "اردو", "नेपाली", "සිංහල"],
  "Middle Eastern": ["فارسی", "עברית", "Türkçe"],
  "European": ["Italiano", "Nederlands", "Polski", "Українська", "Ελληνικά", "Čeština", "Română", "Magyar", "Svenska", "Dansk", "Norsk", "Suomi", "Български", "Hrvatski", "Slovenčina", "Slovenščina", "Српски", "Eesti", "Latviešu", "Lietuvių"],
  "African": ["Kiswahili", "Afrikaans", "አማርኛ"],
  "Other": ["Català", "Galego", "Euskara", "Íslenska", "Македонски", "Қазақша", "Azərbaycan", "Беларуская", "Bosanski", "Հայերdelays", "ქართული", "Cymraeg", "Te Reo Māori"]
};

// Language Showcase Component with expand/collapse
const LanguageShowcase = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const featuredLanguages = ["English", "Español", "हिन्दी", "中文", "العربية", "Português", "日本語", "한국어", "Français", "Deutsch", "தமிழ்", "Русский"];
  
  return (
    <div className="mt-4 w-full max-w-3xl">
      {/* Featured languages always visible */}
      <div className="flex flex-wrap justify-center gap-2">
        {featuredLanguages.map((lang, i) => (
          <span key={i} className="px-3 py-1 text-xs rounded-full bg-white/5 text-viraly-muted border border-white/10 hover:border-viraly-orange/50 hover:text-viraly-light transition-all cursor-default">{lang}</span>
        ))}
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 text-xs rounded-full bg-viraly-orange/20 text-viraly-orange border border-viraly-orange/30 font-medium hover:bg-viraly-orange/30 transition-all flex items-center gap-1"
        >
          {isExpanded ? "Show less" : "+59 more"}
          <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {/* Expandable full list */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              {Object.entries(ALL_LANGUAGES).map(([region, languages]) => (
                <div key={region} className="mb-3 last:mb-0">
                  <h4 className="text-xs font-medium text-viraly-orange mb-2">{region}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((lang, i) => (
                      <span key={i} className="px-2 py-0.5 text-[10px] rounded-full bg-white/5 text-viraly-muted border border-white/5 hover:border-viraly-orange/30 hover:text-viraly-light transition-all cursor-default">{lang}</span>
                    ))}
                  </div>
                </div>
              ))}
              <p className="mt-3 pt-3 border-t border-white/10 text-[10px] text-viraly-muted text-center">
                All languages support text generation & AI voiceovers for videos
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Feature Showcase Cards Component - Rotating cards showing success stories
const FeatureShowcaseCards = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const showcaseItems = [
    {
      title: "Restaurant Success",
      subtitle: "\"Our posts went viral!\"",
      resultImage: "https://static.prod-images.emergentagent.com/jobs/3a699200-5614-40d7-97ed-fd6d1ba6bdca/images/8be2800ff0d1fa86664f646bff0bc369e43b852baa328be459cf35fb1f819b68.png",
      stat: "3x more customers",
      gradient: "from-orange-500/20 to-red-500/20"
    },
    {
      title: "E-commerce Growth",
      subtitle: "\"Sales doubled in a month!\"",
      resultImage: "https://static.prod-images.emergentagent.com/jobs/3a699200-5614-40d7-97ed-fd6d1ba6bdca/images/b39742d42e701de782f65733b99ffda9760237ee59edb5c961ab720cb7810980.png",
      stat: "200% more sales",
      gradient: "from-emerald-500/20 to-teal-500/20"
    },
    {
      title: "Content Creator Win",
      subtitle: "\"Hit 1M followers!\"",
      resultImage: "https://static.prod-images.emergentagent.com/jobs/3a699200-5614-40d7-97ed-fd6d1ba6bdca/images/3775e5bd65fb9d638f52aade3574c77b57aa9d8df8fa7d6cc63e6fe6486160dd.png",
      stat: "10x engagement",
      gradient: "from-purple-500/20 to-pink-500/20"
    },
    {
      title: "Small Business Boom",
      subtitle: "\"Best decision ever!\"",
      resultImage: "https://static.prod-images.emergentagent.com/jobs/3a699200-5614-40d7-97ed-fd6d1ba6bdca/images/46453007562aab498a2e3558aa48e1e329a754e8cb3bdea676bdb4f42fd34aa7.png",
      stat: "5x social reach",
      gradient: "from-blue-500/20 to-cyan-500/20"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % showcaseItems.length);
    }, 3500); // Changed to 3.5 seconds
    return () => clearInterval(interval);
  }, [showcaseItems.length]);

  const currentItem = showcaseItems[currentIndex];

  return (
    <div className="relative w-full max-w-[380px] h-[420px] mx-auto">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.5 }}
          className={`absolute inset-0 rounded-2xl border border-white/10 bg-gradient-to-br ${currentItem.gradient} backdrop-blur-sm overflow-hidden flex flex-col`}
        >
          {/* Result Image */}
          <div className="relative h-[280px] overflow-hidden">
            <img 
              src={currentItem.resultImage} 
              alt={currentItem.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-viraly-dark via-transparent to-transparent" />
            
            {/* Stat Badge */}
            <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-viraly-success/90 rounded-full">
              <span className="text-sm font-bold text-white">{currentItem.stat}</span>
            </div>
          </div>
          
          {/* Text Content */}
          <div className="p-4 flex-1 flex flex-col justify-center">
            <h3 className="font-heading font-bold text-xl mb-1">{currentItem.title}</h3>
            <p className="text-viraly-orange italic">{currentItem.subtitle}</p>
          </div>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 pb-4">
            {showcaseItems.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentIndex ? "w-6 bg-viraly-orange" : "w-1.5 bg-white/20"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const features = [
  {
    icon: Zap,
    title: "Auto-Pilot",
    description: "Set it and forget it! Viraly automatically creates, schedules, and publishes content for you based on your brand profile and industry trends.",
    highlight: true,
    isNew: true,
    isPremium: true
  },
  {
    icon: Link2,
    title: "URL to Post",
    description: "Paste any URL — AI extracts key points and creates platform-ready posts instantly.",
    highlight: true
  },
  {
    icon: Play,
    title: "AI Video Generation",
    description: "Create stunning videos with AI, or upload clips for perfect captions and voiceovers.",
    highlight: true
  },
  {
    icon: Users,
    title: "Multi-Profile / Multi-Brand",
    description: "Manage multiple businesses under one account. Perfect for agencies, freelancers, or entrepreneurs with multiple brands.",
    highlight: true,
    isNew: true
  },
  {
    icon: MessageSquare,
    title: "Voice to Post",
    description: "Just speak your idea — AI transcribes and transforms it into viral-ready content.",
    highlight: true,
    isFree: true
  },
  {
    icon: Target,
    title: "Viral Predictor",
    description: "See viral potential before posting. Get AI-powered hashtag and timing suggestions.",
    isFree: true
  },
  {
    icon: Search,
    title: "Post Analyzer",
    description: "Paste any social post URL — AI identifies what's missing and generates an improved version.",
    isFree: true
  },
  {
    icon: Lightbulb,
    title: "Inspiration Hub",
    description: "Never run out of content ideas! AI prompts, trending topics, content calendar with local holidays, and competitor tracking.",
    isFree: true,
    isNew: true
  },
  {
    icon: Image,
    title: "500 FREE Templates",
    description: "Choose from 500 professional templates across 20 categories. Property, Product, Restaurant, Events, Jobs & more!",
    isFree: true
  },
  {
    icon: Layers,
    title: "Multi-Image Collage",
    description: "Combine up to 6 images into stunning Open House flyers, grid layouts, or hero posts. Perfect for property tours!",
    isFree: true
  },
  {
    icon: Sparkles,
    title: "Brand Learning",
    description: "AI learns your brand voice from any URL. Content that sounds authentically you."
  },
  {
    icon: Zap,
    title: "Ad & Email Generation",
    description: "Generate ad creatives and marketing emails in seconds with one click."
  }
];

// Professions that benefit from Viraly
const professions = [
  { emoji: "🛍️", name: "E-commerce Sellers", color: "from-emerald-500 to-teal-500" },
  { emoji: "🍕", name: "Restaurant Owners", color: "from-orange-500 to-red-500" },
  { emoji: "💅", name: "Beauty & Wellness", color: "from-pink-500 to-rose-500" },
  { emoji: "🏋️", name: "Fitness Coaches", color: "from-blue-500 to-cyan-500" },
  { emoji: "🏠", name: "Real Estate Agents", color: "from-amber-500 to-yellow-500" },
  { emoji: "📸", name: "Photographers", color: "from-purple-500 to-violet-500" },
  { emoji: "🎨", name: "Artists & Designers", color: "from-fuchsia-500 to-pink-500" },
  { emoji: "📚", name: "Course Creators", color: "from-indigo-500 to-blue-500" },
  { emoji: "🎵", name: "Musicians & DJs", color: "from-rose-500 to-orange-500" },
  { emoji: "✈️", name: "Travel Bloggers", color: "from-sky-500 to-blue-500" },
  { emoji: "🏥", name: "Healthcare Providers", color: "from-teal-500 to-green-500" },
  { emoji: "⚖️", name: "Lawyers & Consultants", color: "from-slate-500 to-gray-500" },
  { emoji: "🎬", name: "Content Creators", color: "from-red-500 to-pink-500" },
  { emoji: "🏪", name: "Local Businesses", color: "from-green-500 to-emerald-500" },
  { emoji: "👔", name: "Personal Brands", color: "from-violet-500 to-purple-500" },
  { emoji: "🎓", name: "Educators & Tutors", color: "from-cyan-500 to-teal-500" }
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [referralSettings, setReferralSettings] = useState({
    referrer_reward_posts: 5,
    referee_discount_percent: 10
  });
  const [publicSettings, setPublicSettings] = useState({
    trial_posts: 5,
    trial_days: 7,
    yearly_discount_percent: 35
  });
  const [plans, setPlans] = useState([]);
  
  // Enterprise/Partner Contact Modal States
  const [showEnterpriseContactModal, setShowEnterpriseContactModal] = useState(false);
  const [showPartnerContactModal, setShowPartnerContactModal] = useState(false);
  const [enterpriseFormData, setEnterpriseFormData] = useState({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", message: "" });
  const [partnerFormData, setPartnerFormData] = useState({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", message: "" });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);

  // Fetch referral, loyalty, and pricing settings for dynamic content
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [referralRes, publicRes, loyaltyRes, plansRes] = await Promise.all([
          apiClient.get('/settings/referral-public').catch(() => null),
          apiClient.get('/settings/public').catch(() => null),
          apiClient.get('/settings/loyalty-public').catch(() => null),
          apiClient.get('/settings/plans').catch(() => null)
        ]);
        if (referralRes?.data) {
          setReferralSettings(referralRes.data);
        }
        if (publicRes?.data) {
          setPublicSettings(prev => ({...prev, ...publicRes.data}));
        }
        if (plansRes?.data?.plans) {
          // Filter to only monthly plans (not yearly)
          const monthlyPlans = plansRes.data.plans.filter(p => p.interval === 'month');
          setPlans(monthlyPlans);
          // Also grab yearly discount if available
          if (plansRes.data.yearly_discount_percent) {
            setPublicSettings(prev => ({...prev, yearly_discount_percent: plansRes.data.yearly_discount_percent}));
          }
        }
        if (loyaltyRes?.data?.tiers) {
          // Transform tiers object into sorted array for display
          const tiersArray = Object.entries(loyaltyRes.data.tiers).map(([name, config]) => ({
            tier: name.charAt(0).toUpperCase() + name.slice(1),
            discount: `${config.discount_percent}%`,
            milestone: `$${config.min_spend.toLocaleString()} spent`,
            min_spend: config.min_spend,
            color: name === 'bronze' ? 'from-amber-700 to-amber-600' :
                   name === 'silver' ? 'from-gray-400 to-gray-300' :
                   name === 'gold' ? 'from-yellow-500 to-yellow-400' :
                   name === 'platinum' ? 'from-purple-400 to-blue-400' :
                   'from-emerald-500 to-cyan-500' // diamond
          }));
          // Sort by min_spend ascending
          tiersArray.sort((a, b) => a.min_spend - b.min_spend);
          setLoyaltyTiers(tiersArray);
        }
      } catch (error) {
        console.log('Using default settings');
      }
    };
    fetchSettings();
  }, []);

  // Helper to get plan price for display
  const getPlanPrice = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? `$${Math.round(plan.price)}` : planId === 'starter' ? '$29' : planId === 'growth' ? '$49' : '$99';
  };
  
  const getPlanPosts = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.posts : planId === 'starter' ? 50 : planId === 'growth' ? 100 : 200;
  };
  
  const getPlanVideoCredits = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.video_credits : planId === 'growth' ? 4 : planId === 'pro' ? 8 : 0;
  };

  // Dynamic FAQs with referral settings
  const dynamicFaqs = [
    {
      question: "What is Auto-Pilot and how does it work?",
      answer: "Auto-Pilot is Viraly's automated content creation and publishing system. Set it up once, and Viraly automatically creates, schedules, and publishes content for you based on your brand profile, industry trends, and optimal posting times. You can choose to review posts before they go live, or let them publish automatically."
    },
    {
      question: "How does Viraly work?",
      answer: "Simply paste a URL, upload media, speak your idea, or write content. Our AI analyzes it and creates optimized posts for all 7 platforms: Facebook, Instagram, TikTok, Pinterest, LinkedIn, YouTube, and X. You review, schedule, and we publish automatically. Or enable Auto-Pilot to let Viraly create and post content for you automatically!"
    },
    {
      question: "Can I create posts by speaking?",
      answer: "Yes! Just tap the microphone, speak your idea in any language, and our AI transcribes and transforms it into scroll-stopping content. Auto-stops after 30 seconds to prevent blank recordings."
    },
    {
      question: "What is Brand Learning?",
      answer: "Brand Learning extracts your brand voice, audience, and keywords from any URL. Just paste your website and our AI writes content that sounds like you. Your logo can even be overlaid on images!"
    },
    {
      question: "What platforms do you support?",
      answer: "We support 7 major platforms: Facebook (Pages & Groups), Instagram (Feed & Reels), TikTok, Pinterest, LinkedIn, YouTube, and X (Twitter). We also generate Ad Creatives for Facebook, Instagram, and Google Ads. Connect all your accounts once and post everywhere!"
    },
    {
      question: "How do video credits work?",
      answer: "Video credits are for 10s and 20s videos included in your plan. Growth Plan (4 credits, 720p only) - 10s consumes 1 credit, 20s consumes 2 credits. Pro Plan (8 credits, up to 1080p) - 20s-720p consumes 2 credits, 20s-1080p consumes 4 credits. For longer videos (30s-60s), purchase Extended Video Packs separately."
    },
    {
      question: "What are Pay As You Go Video Credit Packs?",
      answer: "These are additional credits for 10s/20s videos when you've used your monthly plan credits. Quick Pack (2 credits, $4), Standard Pack (4 credits, $8), HD Pack (12 credits for 20s videos, $22.50). Note: HD Pack is for 20s videos only - for 30s+ videos, purchase Extended Video Packs."
    },
    {
      question: "How do regeneration credits work?",
      answer: "Regen credits let you recreate content you're not satisfied with. Image posts: 1 credit. 10s videos: 3 credits. 20s 720p: 6 credits. 20s 1080p: 16 credits. Extended videos (30s+) cannot be regenerated - they're one-time purchases."
    },
    {
      question: "How much does Viraly cost?",
      answer: `Start free with ${publicSettings?.trial_posts || 5} posts — no credit card needed. Plans: ${getPlanPrice('starter')} CAD/month (Starter, ${getPlanPosts('starter')} posts, no videos), ${getPlanPrice('growth')} CAD/month (Growth, ${getPlanPosts('growth')} posts, ${getPlanVideoCredits('growth')} video credits at 720p), or ${getPlanPrice('pro')} CAD/month (Pro, ${getPlanPosts('pro')} posts, ${getPlanVideoCredits('pro')} video credits up to 1080p). Save ${publicSettings?.yearly_discount_percent || 35}% with yearly plans!`
    },
    {
      question: "Can I control what Auto-Pilot posts?",
      answer: "Absolutely! You have full control over Auto-Pilot settings: choose which platforms to post to, how many posts per week, picture or video content, and whether to auto-approve or review each post before publishing. You can also pause or adjust Auto-Pilot anytime."
    },
    {
      question: "Is Viraly good for e-commerce?",
      answer: "Perfect for it! Upload product photos or paste your store URL. AI creates captions with prices, CTAs, and 'Shop Now' links. Works with Shopify, WooCommerce, Etsy, and more."
    },
    {
      question: "What makes Viraly different?",
      answer: "Auto-Pilot for hands-free content creation, AI video generation with quality tiers, voice-to-post, viral score prediction, Post Analyzer, Inspiration Hub (AI prompts, trending topics, content calendar), brand learning, 500 FREE templates, Multi-Profile support for managing multiple brands, personal branding overlays, ad creation, and cross-platform posting — features most competitors don't have."
    },
    {
      question: "What is Multi-Profile and how does it work?",
      answer: "Multi-Profile lets you manage multiple brands or businesses under one Viraly account. Perfect for agencies, freelancers managing clients, or entrepreneurs with multiple ventures. Starter Plan: 2 profiles, Growth Plan: 3 profiles, Pro Plan: 5 profiles. Each profile has its own brand voice, social accounts, Auto-Pilot settings, and content. Credits are shared across all profiles!"
    },
    {
      question: "What is the Inspiration Hub?",
      answer: "A FREE feature that ensures you never run out of content ideas! It includes AI-powered content prompts, a localized Content Calendar with holidays and events for your country, trending topics personalized to your niche, and Competitor Radar to track what others are posting. Competitor monitoring limits: Trial (1), Starter (3), Growth (5), Pro (10)."
    },
    {
      question: "How does the loyalty program work?",
      answer: loyaltyTiers.length > 0
        ? `Our Rolling 12-Month Loyalty Program rewards your spending! Reach ${loyaltyTiers.map(t => `${t.tier} (${t.milestone.replace(' spent', '')})`).join(', ')} based on your spending over the past 12 months. Each tier gives you a permanent discount (${loyaltyTiers.map(t => t.discount).join(', ')}) on ALL future purchases. Discounts stack with promotions and coupons!`
        : "Our Rolling 12-Month Loyalty Program rewards your spending! Reach Bronze ($100), Silver ($300), Gold ($750), Platinum ($1,500), or Diamond ($3,000) tiers based on your spending over the past 12 months. Each tier gives you a permanent discount (5%, 10%, 15%, 20%, 25%) on ALL future purchases. Discounts stack with promotions and coupons!"
    },
    {
      question: "How does the referral program work?",
      answer: `Share your unique referral link or QR code with friends. When they sign up, they get ${referralSettings.referee_discount_percent || 10}% off their first purchase. You earn free post credits for every friend who joins and makes their first purchase! Top referrers can even become Influencers and earn 10% commission on all referred purchases.`
    },
    {
      question: "Can I gift credits to someone?",
      answer: "Yes! Use our 'Gift a Pack' feature to purchase a credit pack for a friend, colleague, or client. They'll receive an email with a gift code valid for 90 days. New users can create an account while redeeming, and existing users get credits added instantly. Perfect for birthdays, holidays, or saying thanks!"
    },
    {
      question: "Can I try before I buy?",
      answer: `Absolutely! Start with ${publicSettings?.trial_posts || 5} free posts — no credit card required. Test our AI, create posts, and see results before committing to a paid plan.`
    },
    {
      question: "How do I get notified when my posts are published?",
      answer: "Enable push notifications in your browser to get instant alerts when your scheduled posts go live. You'll also see notifications in the app (bell icon). We send a weekly digest email summarizing your posts created and published — no spam, just useful updates!"
    },
    {
      question: "What is the Enterprise/B2B Partner Program?",
      answer: "Our Partner Program lets agencies, platforms, and service providers offer Viraly to their clients. Partners earn 10-20% commission on all enterprise subscriptions they bring. Enterprise plans start at 399 CAD/month with 1,000 posts and scale to Infinite (2,999 CAD/month) with unlimited merchants. Contact us for partnership inquiries!"
    },
    {
      question: "How do partner commissions work?",
      answer: "Partners earn commission on every enterprise subscription they refer. Starting at 10% (Level 1), you can grow to 20% (Level 5) as you bring more clients. Commissions are calculated based on 12-month performance from your onboarding date. Payouts are processed via PayPal when you reach the 100 CAD minimum threshold."
    }
  ];

  const handleShare = async () => {
    // On mobile with native share support, use it
    if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      const shareData = {
        title: 'Viraly - AI Social Media Automation',
        text: 'Create, schedule, and publish social media content across all platforms with AI. Try it free! Get 5 free posts to start!',
        url: window.location.origin
      };
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled
      }
    } else {
      // On desktop, show our custom share modal
      setShowShareModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-viraly-dark overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-0 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
                <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
              </div>
              <div className="flex items-center gap-1.5 ml-12">
                <img src="https://flagcdn.com/w20/ca.png" alt="Canada" className="h-3 w-auto" />
                <span className="text-[10px] text-viraly-muted">Proudly Canadian</span>
              </div>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/pricing" className="text-viraly-muted hover:text-white transition-colors">Pricing</Link>
            <a href="#features" className="text-viraly-muted hover:text-white transition-colors">Features</a>
            <a href="#faq" className="text-viraly-muted hover:text-white transition-colors">FAQ</a>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={handleShare}
              className="text-viraly-muted hover:text-white p-2"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
            </Button>
            {user ? (
              <Button 
                onClick={() => navigate("/dashboard")} 
                className="btn-primary px-6 py-2"
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/auth")}
                  className="text-viraly-muted hover:text-white"
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate("/auth?mode=register")} 
                  className="btn-primary px-6 py-2"
                  data-testid="get-started-btn"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - Side by Side layout */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-viraly-orange/10 via-transparent to-transparent opacity-50 z-0" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text */}
            <div className="text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-8">
              <div className="inline-flex items-center gap-2 glass-card px-4 py-2">
                <span className="w-2 h-2 bg-viraly-success rounded-full animate-pulse" />
                <span className="text-sm text-viraly-muted">AI-Powered Social Media Automation</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30">
                <Target className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-semibold text-purple-300">Andromeda Optimized</span>
              </div>
            </div>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl font-bold mb-6 leading-tight"
          >
            One input.
            <br />
            <span className="gradient-text">Endless posts.</span>
          </motion.h1>
          
          {/* New Auto-Pilot tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="text-2xl font-heading font-semibold text-viraly-light mb-3"
          >
            Set it. Forget it. Watch it grow.
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-viraly-muted mb-4 max-w-3xl mx-auto"
          >
            <span className="text-viraly-orange font-semibold">Auto-Create. Auto-Schedule. Auto-Publish.</span>
            <br />
            Or drop a link, website URL, image, text — or just speak.
            <br />
            <span className="text-viraly-light">Viraly handles the rest.</span>
          </motion.p>
          
          {/* Content Types Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-6"
          >
            {[
              { icon: Mic, label: "Voice Input", color: "text-pink-400" },
              { icon: Image, label: "Picture Posts", color: "text-blue-400" },
              { icon: Play, label: "AI Videos", color: "text-purple-400" },
              { icon: MessageSquare, label: "Email Campaigns", color: "text-green-400" },
              { icon: Target, label: "Ads", color: "text-orange-400" }
            ].map((item, i) => (
              <div key={item.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <item.icon className={`w-3.5 h-3.5 ${item.color}`} />
                <span className="text-xs text-viraly-light">{item.label}</span>
              </div>
            ))}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button 
              onClick={() => navigate("/auth?mode=register")}
              className="btn-primary px-8 py-4 text-lg flex items-center gap-2"
              data-testid="hero-cta-btn"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate("/pricing?gift=true")}
              className="btn-secondary px-6 py-4 flex items-center gap-2"
              data-testid="gift-pack-btn"
            >
              <Gift className="w-5 h-5" />
              Gift a Pack
            </Button>
            <Button 
              variant="ghost"
              onClick={handleShare}
              className="btn-secondary px-6 py-4 flex items-center gap-2"
            >
              <Share2 className="w-5 h-5" />
              Refer & Earn
            </Button>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-4 space-y-2"
          >
            <p className="text-viraly-muted">
              5 free posts for new users • No credit card required
            </p>
            <a 
              href="#loyalty-rewards" 
              className="text-sm flex items-center justify-center lg:justify-start gap-2 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <Gift className="w-4 h-4 text-viraly-orange" />
              <span className="text-viraly-orange">Earn points</span>
              <span className="text-viraly-muted">for sharing & referrals — unlock up to 20% off!</span>
            </a>
            {/* How It Works Link */}
            <a 
              href="#how-it-works" 
              className="inline-flex items-center gap-2 text-viraly-orange hover:text-viraly-orange-light transition-colors mt-2"
              data-testid="how-it-works-link"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="text-sm font-medium">How it works →</span>
            </a>
          </motion.div>
            </div>
            
            {/* Right side - Glowing Viraly Logo with Orbiting Icons */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:flex flex-col items-center justify-center"
            >
              {/* Rotating Social Icons */}
              <div className="relative w-72 h-72">
                {/* Center Logo */}
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="relative">
                    <div className="absolute -inset-3 bg-gradient-to-br from-viraly-orange to-orange-600 rounded-2xl blur-lg opacity-50" />
                    <div className="relative w-36 h-36 bg-gradient-to-br from-viraly-orange to-orange-500 rounded-2xl p-1 shadow-2xl">
                      <div className="w-full h-full bg-viraly-dark rounded-xl flex flex-col items-center justify-center">
                        <img src={LOGO_URL} alt="Viraly" className="w-14 h-14 mb-1" />
                        <span className="font-heading font-bold text-base gradient-text">VIRALY</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Rotating Icons Container - Icons positioned in a perfect circle */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                  style={{ transformOrigin: 'center center' }}
                >
                  {/* All icons positioned around a circle - 7 platforms at equal angles */}
                  {/* Radius: 120px so icons touch the outer edge of the 144px container nominally */}
                  {/* Facebook - Position 1 (0 degrees - Top) */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translateY(-120px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* Instagram - Position 2 (~51 degrees) */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 flex items-center justify-center shadow-lg"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(93px, -75px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* TikTok - Position 3 (~103 degrees) */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-gray-900 to-black flex items-center justify-center shadow-lg border border-white/10"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(117px, 27px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* LinkedIn - Position 4 (~154 degrees) - NEW */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-lg"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(49px, 110px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* YouTube - Position 5 (~206 degrees) - NEW */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center shadow-lg"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(-49px, 110px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* X (Twitter) - Position 6 (~257 degrees) - NEW */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-black flex items-center justify-center shadow-lg border border-white/10"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(-117px, 27px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </motion.div>
                  </div>
                  
                  {/* Pinterest - Position 7 (~309 degrees) */}
                  <div 
                    className="absolute w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center shadow-lg"
                    style={{ 
                      top: '50%', 
                      left: '50%',
                      transform: 'translate(-50%, -50%) translate(-93px, -75px)'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/></svg>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
          
          {/* Available Worldwide Badge - Below for side-by-side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 flex flex-col items-center"
          >
            {/* Platform Availability */}
            <div className="inline-flex items-center gap-6 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🌍</span>
                <span className="font-medium text-viraly-light">Available Worldwide</span>
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-viraly-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span className="font-medium text-viraly-light">71 Languages</span>
              </div>
            </div>
            
            {/* Language showcase - expandable */}
            <LanguageShowcase />
            
            <p className="text-sm text-viraly-muted mt-4">Create posts & videos with AI voiceovers in any language</p>
          </motion.div>
        </div>
      </section>

      {/* Who Is This For - Professions Section */}
      <section className="py-16 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Built for <span className="gradient-text">Every Business</span>
            </h2>
            <p className="text-xl text-viraly-muted max-w-2xl mx-auto">
              From local shops to global brands — if you need social media content, Viraly is for you
            </p>
          </motion.div>
        </div>
        
        {/* Animated Marquee */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-viraly-dark to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-viraly-dark to-transparent z-10" />
          
          <motion.div
            animate={{ x: [0, -1920] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="flex gap-4 whitespace-nowrap"
          >
            {[...Array(2)].map((_, setIndex) => (
              <div key={setIndex} className="flex gap-4">
                {[
                  { name: "E-commerce & Retail", highlight: true },
                  { name: "Restaurants & Cafés", highlight: true },
                  { name: "Beauty & Wellness" },
                  { name: "Fitness & Health" },
                  { name: "Real Estate" },
                  { name: "Photography" },
                  { name: "Artists & Designers" },
                  { name: "Course Creators" },
                  { name: "Musicians & DJs" },
                  { name: "Travel & Tourism" },
                  { name: "Healthcare" },
                  { name: "Professional Services" },
                  { name: "Content Creators", highlight: true },
                  { name: "Local Businesses" },
                  { name: "Personal Brands" },
                  { name: "Educators & Coaches" },
                  { name: "Startups & Tech" },
                  { name: "Non-Profits" }
                ].map((prof, i) => (
                  <span
                    key={`${setIndex}-${i}`}
                    className={`px-5 py-2.5 rounded-xl text-base font-medium transition-all ${
                      prof.highlight 
                        ? "bg-gradient-to-r from-viraly-orange/30 to-orange-500/20 text-viraly-orange border border-viraly-orange/30" 
                        : "bg-white/5 text-viraly-light border border-white/10"
                    }`}
                  >
                    {prof.name}
                  </span>
                ))}
              </div>
            ))}
          </motion.div>
        </div>
        
        <p className="text-center mt-8 text-viraly-muted">
          <span className="text-viraly-orange font-semibold">And thousands more!</span> Any business that needs social media presence benefits from Viraly.
        </p>
      </section>

      {/* Stats Section - Only shows when thresholds met */}
      {showStats && stats && (
        <section className="py-16 px-6 border-y border-white/5">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: `${Math.floor(stats.total_creators / 1000)}K+`, label: "Content Creators" },
              { value: `${Math.floor(stats.total_posts / 1000000)}M+`, label: "Posts Generated" },
              { value: `${Math.floor((stats.total_posts || 50000) * 0.15)}+`, label: "Videos Created" },
              { value: `${stats.engagement_multiplier}x`, label: "More Engagement" }
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="text-4xl font-heading font-bold gradient-text mb-2">{stat.value}</p>
                <p className="text-viraly-muted">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Everything you need to
              <br />
              <span className="gradient-text">dominate social media</span>
            </h2>
            <p className="text-viraly-muted text-lg max-w-2xl mx-auto">
              Powerful features that set us apart
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`card-base p-5 hover:border-viraly-orange/30 transition-colors group ${
                  feature.highlight ? "ring-1 ring-viraly-orange/20" : ""
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                  feature.highlight 
                    ? "bg-viraly-gradient" 
                    : "bg-white/5 group-hover:bg-viraly-orange/10 transition-colors"
                }`}>
                  <feature.icon className={`w-5 h-5 ${feature.highlight ? "text-white" : "text-viraly-orange"}`} />
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  {feature.isNew && (
                    <span className="inline-block px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 rounded-full font-medium animate-pulse">
                      NEW
                    </span>
                  )}
                  {feature.isFree && (
                    <span className="inline-block px-2 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded-full font-medium">
                      Free
                    </span>
                  )}
                  {feature.highlight && !feature.isFree && (
                    <span className="inline-block px-2 py-0.5 text-[10px] bg-viraly-orange/20 text-viraly-orange rounded-full font-medium">
                      Exclusive
                    </span>
                  )}
                </div>
                
                <h3 className="font-heading text-lg font-bold mb-1">{feature.title}</h3>
                <p className="text-viraly-muted text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border border-emerald-500/20 p-8 md:p-12"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-viraly-success/20 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4 text-viraly-success" />
                  <span className="text-sm font-medium text-viraly-success">Real Results</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Sell More with AI-Powered Posts
                </h2>
                <p className="text-viraly-muted mb-6">
                  Upload your product photo or paste your store URL. Viraly creates scroll-stopping posts with compelling captions, prices, and calls-to-action.
                </p>
                <div className="space-y-3 mb-6">
                  {[
                    "Works with Shopify, WooCommerce, Etsy, Amazon",
                    "Auto-generates price & 'Shop Now' CTAs",
                    "Create posts in 71 languages for global reach",
                    "Perfect for any business type"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                      <span className="text-viraly-light text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/auth?mode=register">
                  <Button className="btn-primary px-6 py-3">
                    Start Creating for Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              
              {/* Success Stories Showcase */}
              <div className="relative">
                <FeatureShowcaseCards />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Combined Pricing Highlight Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Choose Your <span className="gradient-text">Perfect Plan</span>
            </h2>
            <p className="text-xl text-viraly-muted">Flexible options for every budget</p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Plans Side */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative rounded-3xl bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-violet-500/10 border border-blue-500/20 p-6"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 bg-blue-500/20 px-3 py-1.5 rounded-full">
                    <CreditCard className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-blue-400">Monthly Plans</span>
                  </div>
                  <span className="text-xs text-viraly-success font-medium">Up to 35% off yearly</span>
                </div>
                
                <h3 className="font-heading text-2xl font-bold mb-2">Best Value for Regular Posting</h3>
                <p className="text-viraly-muted text-sm mb-4">Video credits included, higher limits, priority support</p>
                
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {(plans.length > 0 ? [
                    { name: plans.find(p => p.id === 'starter')?.name || "Starter", price: getPlanPrice('starter'), features: `${getPlanPosts('starter')} posts, 2 profiles` },
                    { name: plans.find(p => p.id === 'growth')?.name || "Growth", price: getPlanPrice('growth'), features: `${getPlanPosts('growth')} posts, 3 profiles, ${getPlanVideoCredits('growth')} videos`, popular: true },
                    { name: plans.find(p => p.id === 'pro')?.name || "Pro", price: getPlanPrice('pro'), features: `${getPlanPosts('pro')} posts, 5 profiles, ${getPlanVideoCredits('pro')} HD videos` }
                  ] : [
                    { name: "Starter", price: "$29", features: "50 posts, 2 profiles" },
                    { name: "Growth", price: "$49", features: "100 posts, 3 profiles, 4 videos", popular: true },
                    { name: "Pro", price: "$99", features: "200 posts, 5 profiles, 8 HD videos" }
                  ]).map((plan, i) => (
                    <div key={i} className={`card-base p-3 text-center ${plan.popular ? 'ring-2 ring-blue-500' : ''}`}>
                      {plan.popular && (
                        <span className="inline-block px-2 py-0.5 text-[10px] bg-blue-500 text-white rounded-full mb-1">Popular</span>
                      )}
                      <p className="font-heading font-bold text-lg">{plan.name}</p>
                      <p className="text-xl font-bold text-blue-400">{plan.price}</p>
                      <p className="text-[10px] text-viraly-muted">{plan.features}</p>
                    </div>
                  ))}
                </div>
                
                <Link to="/pricing?tab=subscription" className="block">
                  <Button className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-400">
                    View Monthly Plans
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            {/* Pay As You Go Side */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="relative rounded-3xl bg-gradient-to-br from-viraly-orange/20 via-orange-500/10 to-yellow-500/10 border border-viraly-orange/20 p-6"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-viraly-orange/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 bg-viraly-orange/20 px-3 py-1.5 rounded-full">
                    <Wallet className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm font-medium text-viraly-orange">Pay As You Go</span>
                  </div>
                  <span className="text-xs text-viraly-muted">90-day validity</span>
                </div>
                
                <h3 className="font-heading text-2xl font-bold mb-2">No Commitment Needed</h3>
                <p className="text-viraly-muted text-sm mb-4">Perfect for testing, seasonal campaigns, or one-time projects</p>
                
                <div className="mb-3">
                  <p className="text-xs text-viraly-muted mb-2 uppercase tracking-wide font-medium">Post Credits</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { qty: "5", price: "$5" },
                      { qty: "15", price: "$12" },
                      { qty: "30", price: "$20" },
                      { qty: "100", price: "$50", best: true }
                    ].map((p, i) => (
                      <div key={i} className={`card-base p-2 text-center ${p.best ? 'ring-1 ring-viraly-orange' : ''}`}>
                        <p className="text-sm font-bold">{p.qty}</p>
                        <p className="text-xs text-viraly-orange">{p.price}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs text-viraly-muted mb-2 uppercase tracking-wide font-medium flex items-center gap-1">
                    <Video className="w-3 h-3 text-purple-400" /> Video Credits
                  </p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { qty: "3", price: "$3.75", name: "Quick" },
                      { qty: "6", price: "$7.50", name: "Standard" },
                      { qty: "12", price: "$22.50", name: "HD", best: true }
                    ].map((p, i) => (
                      <div key={i} className={`card-base p-2 text-center ${p.best ? 'ring-1 ring-purple-500' : ''}`}>
                        <p className="text-sm font-bold">{p.qty}</p>
                        <p className="text-xs text-purple-400">{p.price}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Link to="/pricing?tab=payg" className="block">
                  <Button className="w-full btn-primary">
                    View All Credit Packs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 bg-viraly-card/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              How it works
            </h2>
            <p className="text-viraly-muted text-lg">Three steps to automation</p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Add Your Content", desc: "Paste a URL, upload media, or write your message", icons: [Link2, Image, Video] },
              { step: "02", title: "AI Creates Posts", desc: "Get optimized captions, hashtags, and visuals for each platform" },
              { step: "03", title: "Auto Publish", desc: "Review once, we schedule and post automatically" }
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-6xl font-heading font-bold text-viraly-orange/20 mb-4">{item.step}</div>
                <h3 className="font-heading text-2xl font-bold mb-2">{item.title}</h3>
                <p className="text-viraly-muted">{item.desc}</p>
                
                {item.icons && (
                  <div className="flex gap-2 mt-4">
                    {item.icons.map((Icon, j) => (
                      <div key={j} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-viraly-orange" />
                      </div>
                    ))}
                  </div>
                )}
                
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-viraly-orange/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Across & Content Radar - Hidden for MVP launch */}
      {/* These features will be enabled in a future update
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="card-base p-8"
            >
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-6">
                <Quote className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Quote Across</h3>
              <p className="text-viraly-muted mb-4">
                Found a post you love? A tweet that inspires?
              </p>
              <p className="text-viraly-light mb-6">
                <span className="text-viraly-orange">Just paste the link.</span> We'll create your own unique version for all platforms.
              </p>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-sm">Paste trending tweet → Get your take for all 7 platforms</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="card-base p-8"
            >
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Content Radar</h3>
              <p className="text-viraly-muted mb-4">
                <span className="text-viraly-light">Auto competitor monitoring.</span> Add their profiles, get alerts when they post.
              </p>
              <p className="text-viraly-light mb-6">
                Get AI-suggested responses or match their style. <span className="text-viraly-orange">Stay ahead.</span>
              </p>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-sm"><strong>Quote Across</strong> = You find & paste once</p>
                <p className="text-sm"><strong>Content Radar</strong> = We monitor automatically</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      */}

      {/* Loyalty Rewards */}
      <section id="loyalty-rewards" className="py-24 px-6 bg-viraly-card/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mx-auto mb-6">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Customer Loyalty <span className="gradient-text">Rewards</span>
            </h2>
            <p className="text-viraly-muted text-lg max-w-2xl mx-auto">
              Reach spending milestones and unlock discounts on all future purchases
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(loyaltyTiers.length > 0 ? loyaltyTiers : [
              { tier: "Bronze", discount: "5%", milestone: "$100 spent", color: "from-amber-700 to-amber-600" },
              { tier: "Silver", discount: "10%", milestone: "$300 spent", color: "from-gray-400 to-gray-300" },
              { tier: "Gold", discount: "15%", milestone: "$750 spent", color: "from-yellow-500 to-yellow-400" },
              { tier: "Platinum", discount: "20%", milestone: "$1,500 spent", color: "from-purple-400 to-blue-400" },
              { tier: "Diamond", discount: "25%", milestone: "$3,000 spent", color: "from-emerald-500 to-cyan-500" }
            ]).map((item, i) => (
              <motion.div
                key={item.tier}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="card-base p-5 text-center"
              >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-3`}>
                  <Star className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading font-bold mb-1">{item.tier}</h3>
                <p className="text-2xl font-heading font-bold gradient-text mb-2">{item.discount} OFF</p>
                <p className="text-xs text-viraly-muted">{item.milestone}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-6 text-xs text-viraly-muted max-w-xl mx-auto"
          >
            * Discounts apply to all credit purchases after reaching the milestone. Based on rolling 12-month spend. 
            Loyalty tier benefits are subject to change. See <Link to="/terms" className="text-viraly-orange hover:underline">Terms & Conditions</Link> for details.
          </motion.p>
        </div>
      </section>

      {/* Influencer Leaderboard */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent to-viraly-dark/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-viraly-orange to-purple-500 flex items-center justify-center mx-auto mb-4">
              <Award className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
              Top Influencers
            </h2>
            <p className="text-viraly-muted text-sm">Earn lifetime commissions by referring others</p>
          </motion.div>

          <InfluencerLeaderboard />
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-6 text-xs text-viraly-muted"
          >
            Become an influencer by referring customers. <Link to="/rewards" className="text-viraly-orange hover:underline">Learn more</Link>
          </motion.p>
        </div>
      </section>

      {/* Customer Reviews */}
      <TestimonialsSection />

      {/* Technology Partners */}
      <TechnologyPartners />
      
      {/* Enterprise & Partner Section */}
      <section id="enterprise" className="py-24 px-6 bg-gradient-to-b from-viraly-dark via-purple-950/20 to-viraly-dark">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-purple-400" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Enterprise & <span className="gradient-text">Partner</span> Solutions
            </h2>
            <p className="text-viraly-muted max-w-2xl mx-auto">
              Scale your business or become a Viraly partner. We offer white-label API access, dedicated support, and industry-leading margins.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Enterprise Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="card-base p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-viraly-light">Enterprise API</h3>
                  <p className="text-sm text-viraly-muted">For payment gateways, CRMs, and platforms</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  White-label API access for your merchants
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  Flexible pricing plans (Builder, Scaler, Infinite)
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  24x7 support through Vira On-Call
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  GDPR, CCPA, PIPEDA, LGPD compliant
                </li>
              </ul>
              
              <Button
                className="w-full btn-primary"
                onClick={() => setShowEnterpriseContactModal(true)}
              >
                Contact Sales
              </Button>
            </motion.div>
            
            {/* Partner Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="card-base p-8 border border-blue-500/20 hover:border-blue-500/40 transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Handshake className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-viraly-light">Become a Partner</h3>
                  <p className="text-sm text-viraly-muted">Channel, agency, and referral partners</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  20% lifetime commission on referrals
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  Dedicated partner portal & tracking
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  Co-marketing opportunities
                </li>
                <li className="flex items-center gap-2 text-sm text-viraly-light">
                  <Check className="w-4 h-4 text-green-400" />
                  Regional territory exclusivity available
                </li>
              </ul>
              
              <Button
                className="w-full btn-secondary"
                onClick={() => setShowPartnerContactModal(true)}
              >
                Apply to Partner Program
              </Button>
            </motion.div>
          </div>
          
          <p className="text-center text-viraly-muted text-sm">
            Already a partner? <Link to="/partners" className="text-viraly-orange hover:underline">Login to Partner Portal →</Link>
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-viraly-orange" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-viraly-muted">Everything you need to know</p>
          </motion.div>

          <div className="space-y-4">
            {dynamicFaqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                viewport={{ once: true }}
                className="card-base overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-viraly-muted flex-shrink-0 transition-transform ${
                      expandedFaq === i ? "rotate-180" : ""
                    }`} 
                  />
                </button>
                <AnimatePresence>
                  {expandedFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-viraly-muted">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-viraly-card/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="glass-card-heavy p-12 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-viraly-orange/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                Ready to go <span className="gradient-text">viral</span>?
              </h2>
              <p className="text-viraly-muted text-lg mb-8 max-w-xl mx-auto">
                Join creators who've automated their social media. 5 free posts to start.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  onClick={() => navigate("/auth?mode=register")}
                  className="btn-primary px-8 py-4 text-lg"
                  data-testid="cta-btn"
                >
                  Start Your Free Trial
                </Button>
                <Button 
                  variant="ghost"
                  onClick={handleShare}
                  className="btn-secondary px-6 py-4 flex items-center gap-2"
                >
                  <Trophy className="w-5 h-5" />
                  Refer and Earn
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 mt-8 text-sm text-viraly-muted">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-viraly-success" /> 5 free posts
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-viraly-success" /> No credit card
                </span>
                <span className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-viraly-orange" /> Earn rewards
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chatbot is now global via ViraChatbot component in App.js */}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Viraly" className="h-8 w-8" />
              <span className="font-heading font-bold text-xl">Viraly</span>
            </div>
            
            <div className="flex items-center gap-8 text-sm text-viraly-muted">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <a href="mailto:care@viraly.ca" className="hover:text-white transition-colors flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                care@viraly.ca
              </a>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap justify-center md:justify-end">
              {/* Compliance Badges */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" title="GDPR Compliant - EU Data Protection">
                <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-xs text-viraly-muted">GDPR</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" title="CCPA Compliant - California Privacy">
                <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-xs text-viraly-muted">CCPA</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" title="PIPEDA Compliant - Canadian Privacy">
                <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-xs text-viraly-muted">PIPEDA</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" title="LGPD Compliant - Brazil Privacy">
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-xs text-viraly-muted">LGPD</span>
              </div>
              <p className="text-viraly-muted text-sm ml-2">
                © 2026 Viraly
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)}
        shareData={{
          title: "Viraly - AI Social Media Automation",
          text: "I'm using Viraly to create amazing social media content with AI! Check it out - get 5 free posts to start!",
          url: window.location.origin
        }}
      />
      
      {/* Enterprise Contact Modal */}
      <AnimatePresence>
        {showEnterpriseContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEnterpriseContactModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md p-6 border border-purple-500/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-viraly-light">Enterprise Inquiry</h3>
                    <p className="text-xs text-viraly-muted">White-label API access for your business</p>
                  </div>
                </div>
                <button onClick={() => setShowEnterpriseContactModal(false)} className="text-viraly-muted hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Company Name *</label>
                  <Input
                    value={enterpriseFormData.company_name}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, company_name: e.target.value })}
                    placeholder="Your Company Inc."
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Your Name *</label>
                  <Input
                    value={enterpriseFormData.contact_name}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Email *</label>
                  <Input
                    type="email"
                    value={enterpriseFormData.email}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, email: e.target.value })}
                    placeholder="you@company.com"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Message (optional)</label>
                  <Textarea
                    value={enterpriseFormData.message}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, message: e.target.value })}
                    placeholder="Tell us about your use case..."
                    rows={3}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>
              
              <Button
                className="w-full btn-primary mt-6"
                disabled={submittingInquiry}
                onClick={async () => {
                  if (!enterpriseFormData.company_name || !enterpriseFormData.contact_name || !enterpriseFormData.email) {
                    toast.error("Please fill all required fields");
                    return;
                  }
                  setSubmittingInquiry(true);
                  try {
                    await apiClient.post("/api/b2b/contact", { ...enterpriseFormData, inquiry_type: "enterprise" });
                    toast.success("Thank you! Our team will contact you within 24 hours.");
                    setShowEnterpriseContactModal(false);
                    setEnterpriseFormData({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", message: "" });
                  } catch (err) {
                    toast.error("Failed to submit. Please try again.");
                  }
                  setSubmittingInquiry(false);
                }}
              >
                {submittingInquiry ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Partner Contact Modal */}
      <AnimatePresence>
        {showPartnerContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPartnerContactModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md p-6 border border-blue-500/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Handshake className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-viraly-light">Partner Application</h3>
                    <p className="text-xs text-viraly-muted">Earn 20% lifetime commission</p>
                  </div>
                </div>
                <button onClick={() => setShowPartnerContactModal(false)} className="text-viraly-muted hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Company / Agency Name *</label>
                  <Input
                    value={partnerFormData.company_name}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, company_name: e.target.value })}
                    placeholder="Your Agency Inc."
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Your Name *</label>
                  <Input
                    value={partnerFormData.contact_name}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, contact_name: e.target.value })}
                    placeholder="Jane Doe"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Email *</label>
                  <Input
                    type="email"
                    value={partnerFormData.email}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, email: e.target.value })}
                    placeholder="you@agency.com"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Tell us about your business (optional)</label>
                  <Textarea
                    value={partnerFormData.message}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, message: e.target.value })}
                    placeholder="How many clients do you serve? What's your target market?"
                    rows={3}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>
              
              <Button
                className="w-full btn-primary mt-6"
                disabled={submittingInquiry}
                onClick={async () => {
                  if (!partnerFormData.company_name || !partnerFormData.contact_name || !partnerFormData.email) {
                    toast.error("Please fill all required fields");
                    return;
                  }
                  setSubmittingInquiry(true);
                  try {
                    await apiClient.post("/api/b2b/contact", { ...partnerFormData, inquiry_type: "partner" });
                    toast.success("Thank you! We'll review your application and get back to you within 48 hours.");
                    setShowPartnerContactModal(false);
                    setPartnerFormData({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", message: "" });
                  } catch (err) {
                    toast.error("Failed to submit. Please try again.");
                  }
                  setSubmittingInquiry(false);
                }}
              >
                {submittingInquiry ? "Submitting..." : "Apply Now"}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Scroll to Top Button */}
      <ScrollToTopButton />
    </div>
  );
}
